import React, { useState } from 'react';

import { Alert, Button, Group, Stack, TextInput } from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconAlertTriangle } from '@tabler/icons-react';
import 'react-international-phone/style.css';
import useVerifyEmail from 'hooks/auth/useVerifyEmail';
import { emailValidator } from 'utils/form-validators';

import { AccountControllerStates } from '../AccountController';

interface IEmailVerificationProps {
  email: string;
  setVerifiedEmail: React.Dispatch<React.SetStateAction<string>>;
  setState: (state: AccountControllerStates) => void;
}

interface FormValues {
  email: string;
}
const EmailVerification = ({ email, setVerifiedEmail, setState }: IEmailVerificationProps): JSX.Element => {
  const verifyEmailMutation = useVerifyEmail();
  // The lookup service failing is not the same as "email exists" (spec Item 2):
  // the buyer can retry, or continue anyway and be verified at sign-in instead
  // of being blocked from checkout entirely.
  const [lookupFailed, setLookupFailed] = useState<boolean>(false);
  const form = useForm<FormValues>({
    initialValues: {
      email: email,
    },
    validate: {
      email: value => emailValidator(value),
    },
  });
  const handleEmailChange = (e: string): void => {
    form.clearErrors();
    setLookupFailed(false);
    form.setFieldValue('email', e);
  };
  const handleContinueAnyway = (): void => {
    setVerifiedEmail(form.values.email);
    setState('Create Account');
  };
  const handleSubmit = (formValues: FormValues): void => {
    setLookupFailed(false);
    verifyEmailMutation.mutate(formValues, {
      onSuccess: () => {
        setVerifiedEmail(formValues.email);
        setState('Create Account');
      },
      onError: error => {
        // The check returns 400 exactly when the email is registered; anything
        // else (network, server error) means the lookup itself failed.
        if (error.response?.status === 400) {
          form.setFieldError(
            'email',
            'An account with this email already exists. Sign in instead or reset your password.',
          );
        } else {
          setLookupFailed(true);
        }
      },
    });
  };
  return (
    <form onSubmit={form.onSubmit(handleSubmit)}>
      <Stack>
        <Stack px='xl'>
          <TextInput
            {...form.getInputProps('email')}
            label='Email'
            placeholder='jane@mygym.com'
            data-cy='userInput'
            required
            onChange={e => handleEmailChange(e.currentTarget.value)}
          />
          {lookupFailed && (
            <Alert icon={<IconAlertTriangle size='1rem' />} color='yellow' data-cy='emailLookupFailure'>
              We couldn&apos;t check this email right now. Retry — or continue anyway; we&apos;ll verify your
              account at sign-in.
            </Alert>
          )}
        </Stack>
        <Group position='right'>
          {!!Object.keys(form.errors).length && <Button onClick={() => setState('Login')}>Log in</Button>}
          {lookupFailed ? (
            <>
              <Button variant='default' data-cy='emailLookupContinueAnyway' onClick={handleContinueAnyway}>
                Continue anyway
              </Button>
              <Button type='submit' data-cy='emailLookupRetry' loading={verifyEmailMutation.isLoading}>
                Retry
              </Button>
            </>
          ) : (
            <Button
              type='submit'
              disabled={!!Object.keys(form.errors).length}
              loading={verifyEmailMutation.isLoading}
            >
              Continue
            </Button>
          )}
        </Group>
      </Stack>
    </form>
  );
};
export default EmailVerification;
